// indexedDBService.ts
import { Table, Product, Category, Order, PaymentMethod } from '../types';

const DB_NAME = 'restaurantDB';
const DB_VERSION = 3; // Increase version to trigger an upgrade

let db: IDBDatabase | null = null;

// Função para abrir o banco de dados
const openDatabase = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    console.log('Opening IndexedDB database...');
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('Error opening database:', (event.target as IDBRequest).error);
      reject((event.target as IDBRequest).error);
    };

    request.onsuccess = (event) => {
      db = (event.target as IDBOpenDBRequest).result;
      console.log('Database opened successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      
      console.log(`Database upgrade needed from ${event.oldVersion} to ${event.newVersion}`);

      // Create or update object stores as needed
      if (!database.objectStoreNames.contains('tables')) {
        database.createObjectStore('tables', { keyPath: 'id' });
        console.log('Created tables store');
      }
      
      if (!database.objectStoreNames.contains('products')) {
        database.createObjectStore('products', { keyPath: 'id' });
        console.log('Created products store');
      }
      
      if (!database.objectStoreNames.contains('categories')) {
        database.createObjectStore('categories', { keyPath: 'id' });
        console.log('Created categories store');
      }
      
      if (!database.objectStoreNames.contains('orders')) {
        database.createObjectStore('orders', { keyPath: 'id' });
        console.log('Created orders store');
      }
      
      // Create payment_methods store that was missing
      if (!database.objectStoreNames.contains('payment_methods')) {
        database.createObjectStore('payment_methods', { keyPath: 'id' });
        console.log('Created payment_methods store');
      }
      
      if (!database.objectStoreNames.contains('settings')) {
        database.createObjectStore('settings', { keyPath: 'key' });
        console.log('Created settings store');
      }
    };
  });
};

// Inicializar o banco de dados
export const initDatabase = async (): Promise<void> => {
  try {
    await openDatabase();
    console.log('Database initialized successfully!');
  } catch (error) {
    console.error('Error initializing database:', error);
    throw error;
  }
};

// Clear all records from a specific store
export const clearStore = async (storeName: string): Promise<void> => {
  try {
    const db = await openDatabase();
    
    try {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      
      const request = store.clear();
      
      return new Promise<void>((resolve, reject) => {
        request.onsuccess = () => {
          console.log(`Successfully cleared all records from ${storeName}`);
          resolve();
        };
        request.onerror = () => {
          console.error(`Error clearing records from ${storeName}:`, request.error);
          reject(new Error(`Error clearing records from ${storeName}`));
        };
      });
    } catch (transactionError) {
      console.error(`Error creating transaction for ${storeName}:`, transactionError);
      throw transactionError;
    }
  } catch (error) {
    console.error(`Error clearing records from ${storeName}:`, error);
    throw error;
  }
};

// Make sure all objects have valid IDs before saving
const ensureValidIds = <T extends { id?: string }>(items: T[]): T[] => {
  return items.map(item => {
    // If id is missing or empty, generate a random one
    if (!item.id) {
      const randomId = Math.random().toString(36).substring(2, 15);
      console.log(`Generated ID for item:`, randomId);
      return { ...item, id: randomId };
    }
    return item;
  });
};

// Salvar múltiplos itens em um objectStore
export const saveItems = async <T extends { id?: string }>(storeName: string, items: T[]): Promise<void> => {
  try {
    const db = await openDatabase();
    
    // Ensure all items have valid IDs
    const validItems = ensureValidIds(items);
    
    // Log some items for debugging
    console.log(`Saving ${validItems.length} items to ${storeName}`);
    if (validItems.length > 0) {
      console.log('Sample item structure:', JSON.stringify(validItems[0]).substring(0, 100) + '...');
    }
    
    try {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      
      validItems.forEach(item => {
        try {
          store.put(item);
        } catch (itemError) {
          console.error(`Error putting item in ${storeName}:`, itemError, item);
        }
      });
      
      return new Promise<void>((resolve, reject) => {
        transaction.oncomplete = () => {
          console.log(`Successfully saved ${validItems.length} items to ${storeName}`);
          resolve();
        };
        transaction.onerror = (event) => {
          console.error(`Error in transaction for ${storeName}:`, event);
          reject(new Error(`Error saving items to ${storeName}`));
        };
      });
    } catch (transactionError) {
      console.error(`Error creating transaction for ${storeName}:`, transactionError);
      throw transactionError;
    }
  } catch (error) {
    console.error(`Error saving items to ${storeName}:`, error);
    throw error;
  }
};

// Obter todos os itens de um objectStore
export const getItems = async <T>(storeName: string): Promise<T[]> => {
  try {
    const db = await openDatabase();
    
    try {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      
      const request = store.getAll();
      
      return new Promise<T[]>((resolve, reject) => {
        request.onsuccess = () => {
          console.log(`Retrieved ${request.result?.length || 0} items from ${storeName}`);
          resolve(request.result || []);
        };
        request.onerror = () => {
          console.error(`Error getting items from ${storeName}:`, request.error);
          reject(new Error(`Error getting items from ${storeName}`));
        };
      });
    } catch (transactionError) {
      console.error(`Error creating transaction for ${storeName}:`, transactionError);
      return [];
    }
  } catch (error) {
    console.error(`Error getting items from ${storeName}:`, error);
    return [];
  }
};

// Obter um item específico de um objectStore pelo ID
export const getItemById = async <T>(storeName: string, id: string): Promise<T | undefined> => {
  try {
    const db = await openDatabase();
    
    try {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      
      const request = store.get(id);
      
      return new Promise<T | undefined>((resolve, reject) => {
        request.onsuccess = () => {
          resolve(request.result);
        };
        request.onerror = () => {
          console.error(`Error getting item ${id} from ${storeName}:`, request.error);
          reject(new Error(`Error getting item ${id} from ${storeName}`));
        };
      });
    } catch (transactionError) {
      console.error(`Error creating transaction for ${storeName}:`, transactionError);
      return undefined;
    }
  } catch (error) {
    console.error(`Error getting item ${id} from ${storeName}:`, error);
    return undefined;
  }
};

// Remover um item de um objectStore pelo ID
export const removeItem = async (storeName: string, id: string): Promise<void> => {
  try {
    const db = await openDatabase();
    
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const request = store.delete(id);
    
    return new Promise<void>((resolve, reject) => {
      request.onsuccess = () => {
        console.log(`Item ${id} removed from ${storeName}`);
        resolve();
      };
      request.onerror = () => {
        console.error(`Error removing item ${id} from ${storeName}:`, request.error);
        reject(new Error(`Error removing item ${id} from ${storeName}`));
      };
    });
  } catch (error) {
    console.error(`Error removing item ${id} from ${storeName}:`, error);
    throw error;
  }
};

// Salvar uma configuração
export const saveSetting = async (key: string, value: string): Promise<void> => {
  try {
    const db = await openDatabase();
    
    const transaction = db.transaction('settings', 'readwrite');
    const store = transaction.objectStore('settings');
    
    store.put({ key, value });
    
    return new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => {
        console.log(`Setting ${key} saved successfully`);
        resolve();
      };
      transaction.onerror = () => {
        console.error('Error saving setting:', transaction.error);
        reject(new Error('Error saving configuration'));
      };
    });
  } catch (error) {
    console.error('Error saving configuration:', error);
    throw error;
  }
};

// Obter uma configuração
export const getSetting = async (key: string): Promise<string | null> => {
  try {
    const db = await openDatabase();
    
    const transaction = db.transaction('settings', 'readonly');
    const store = transaction.objectStore('settings');
    
    const request = store.get(key);
    
    return new Promise<string | null>((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
      request.onerror = () => {
        console.error('Error getting setting:', request.error);
        reject(new Error('Error getting configuration'));
      };
    });
  } catch (error) {
    console.error('Error getting configuration:', error);
    return null;
  }
};

// Obter as configurações da API (baseURL e apiKey)
export const getAPIConfig = async (): Promise<{ baseURL: string; apiKey: string }> => {
  try {
    const baseURL = await getSetting('api_base_url') || '';
    const apiKey = await getSetting('api_key') || '';
    return { baseURL, apiKey };
  } catch (error) {
    console.error('Error getting API settings:', error);
    return { baseURL: '', apiKey: '' };
  }
};

// Atualizar as configurações da API (baseURL e apiKey)
export const updateAPIConfig = async (baseURL: string, apiKey: string): Promise<void> => {
  try {
    await saveSetting('api_base_url', baseURL);
    await saveSetting('api_key', apiKey);
    console.log('API configuration updated successfully');
  } catch (error) {
    console.error('Error updating API settings:', error);
    throw error;
  }
};

// Migrar dados do localStorage para o IndexedDB
export const migrateFromLocalStorage = async (): Promise<void> => {
  try {
    console.log('Migrating data from localStorage to IndexedDB...');
    
    // Tables
    const tablesJson = localStorage.getItem('restaurant_tables');
    if (tablesJson) {
      try {
        const tables: Table[] = JSON.parse(tablesJson);
        await saveItems('tables', tables);
        localStorage.removeItem('restaurant_tables');
      } catch (error) {
        console.error('Error migrating tables:', error);
      }
    }
    
    // Products
    const productsJson = localStorage.getItem('restaurant_products');
    if (productsJson) {
      try {
        const products: Product[] = JSON.parse(productsJson);
        await saveItems('products', products);
        localStorage.removeItem('restaurant_products');
      } catch (error) {
        console.error('Error migrating products:', error);
      }
    }
    
    // Categories
    const categoriesJson = localStorage.getItem('restaurant_categories');
    if (categoriesJson) {
      try {
        const categories: Category[] = JSON.parse(categoriesJson);
        await saveItems('categories', categories);
        localStorage.removeItem('restaurant_categories');
      } catch (error) {
        console.error('Error migrating categories:', error);
      }
    }
    
    // Orders
    const ordersJson = localStorage.getItem('restaurant_orders');
    if (ordersJson) {
      try {
        const orders: Order[] = JSON.parse(ordersJson);
        await saveItems('orders', orders);
        localStorage.removeItem('restaurant_orders');
      } catch (error) {
        console.error('Error migrating orders:', error);
      }
    }
    
    // Payment Methods
    const paymentMethodsJson = localStorage.getItem('payment_methods');
    if (paymentMethodsJson) {
      try {
        const paymentMethods: PaymentMethod[] = JSON.parse(paymentMethodsJson);
        await saveItems('payment_methods', paymentMethods);
        localStorage.removeItem('payment_methods');
      } catch (error) {
        console.error('Error migrating payment methods:', error);
      }
    }
    
    console.log('Migration from localStorage to IndexedDB completed successfully!');
  } catch (error) {
    console.error('Error migrating data from localStorage to IndexedDB:', error);
    throw error;
  }
};

// Fix the database by removing and recreating it
export const resetDatabase = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Close current connection
    if (db) {
      db.close();
      db = null;
    }
    
    // Delete the database
    const request = indexedDB.deleteDatabase(DB_NAME);
    
    request.onsuccess = async () => {
      console.log('Database deleted successfully, reinitializing...');
      try {
        await initDatabase();
        resolve();
      } catch (error) {
        reject(error);
      }
    };
    
    request.onerror = () => {
      console.error('Error deleting database:', request.error);
      reject(new Error('Failed to reset database'));
    };
  });
};
